import { useRef, useState } from 'react';
import PeriodPicker from './PeriodPicker';
import { PRESETS_MONTHLY } from '../utils/period';
import MultiSelectFilter from './MultiSelectFilter';
import FilterButton from './FilterButton';
import FilterSheet from './FilterSheet';
import { SortOptions } from './MobileSort';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import { PAYMENT_METHOD_OPTIONS } from '../pages/SupplierManagementPage';
import Icon from './Icon';

// Payment method is the primary categorical filter here. There is deliberately
// NO status filter: the payments screen is locked to שולם + שולם חלקית (see
// LOCKED_STATUSES in PaymentsPage) — fully-unpaid suppliers never appear.
// Method options come from the shared PAYMENT_METHOD_OPTIONS so הוראת קבע (which
// auto-pay writes) is included; matching against stored values is done with
// apostrophe/geresh normalization in PaymentsPage.
//
// ≤720px the bar collapses to search + a "סינון" trigger; method and the
// date controls move into a FilterSheet (bottom sheet).
const METHOD_OPTIONS = PAYMENT_METHOD_OPTIONS.map(o => o.value);

export default function PaymentsFilterBar({
  nameFilter,   setNameFilter,
  methodFilter, setMethodFilter,
  dateFrom,     dateTo,
  setDateRange,
  onClearAll,
  resultCount,
  // Sort state, data-driven — folded into the same "סינון ומיון" sheet as the
  // other filters on mobile, same pattern as SuppliersFilterBar. Desktop has
  // no sort control here (this page has none there either).
  sortOptions,
  sort,
  onSortChange,
}) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const wrapRef = useRef(null);
  useStickyFilterOffset(wrapRef);

  const hasChips = Boolean(
    nameFilter ||
    (methodFilter && methodFilter.length)
  );
  const sheetActiveCount = (methodFilter && methodFilter.length > 0) ? 1 : 0;

  const searchPill = (
    <label className={`docs-filter-control docs-filter-searchpill${nameFilter ? ' is-active' : ''}`}>
      <Icon name="search" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        placeholder="חפש לפי שם ספק"
        style={{ minWidth: isMobile ? 0 : 160 }}
      />
    </label>
  );

  const methodSelect = (inline) => (
    <MultiSelectFilter
      inline={inline}
      label="אמצעי תשלום"
      values={methodFilter}
      options={METHOD_OPTIONS}
      onChange={setMethodFilter}
    />
  );
  // One date control for both months and arbitrary day ranges. PRESETS_MONTHLY
  // because /suppliers/paid shares the month-scoped server semantics of
  // /suppliers — see server/lib/monthKey.js.
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom}
      dateTo={dateTo}
      onChange={(f, t) => setDateRange(f, t)}
      presets={PRESETS_MONTHLY}
      variant={variant}
    />
  );

  const renderChips = () => (
    <div className="docs-filter-chips">
      {nameFilter && (
        <span className="docs-filter-chip">
          חיפוש: {nameFilter}
          <button type="button" aria-label="נקה חיפוש"
            onClick={() => setNameFilter('')} className="docs-filter-chip-x"><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {methodFilter && methodFilter.length > 0 && (
        <span className="docs-filter-chip">
          אמצעי תשלום: {methodFilter.slice(0, 3).join(', ')}
          {methodFilter.length > 3 && ` +${methodFilter.length - 3}`}
          <button type="button" aria-label="נקה אמצעי תשלום"
            onClick={() => setMethodFilter([])} className="docs-filter-chip-x"><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={onClearAll}>
        נקה הכל
      </button>
    </div>
  );

  if (isMobile) {
    const hasSort = !!(sort && sort.field);
    const combinedActiveCount = sheetActiveCount + (hasSort ? 1 : 0);
    return (
      <>
      <div className="period-bar" ref={wrapRef}>{dateRange('bar')}</div>
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {searchPill}
          <FilterButton
            label="סינון ומיון"
            activeCount={combinedActiveCount}
            expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          />
        </div>
        {hasChips && renderChips()}

        <FilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="סינון ומיון"
          resultCount={resultCount}
          onClearAll={onClearAll}
        >
          {sortOptions && sortOptions.length > 0 && (
            <>
              <div className="filter-sheet-section-label">מיון</div>
              <SortOptions options={sortOptions} sort={sort} onChange={onSortChange} />
            </>
          )}
          {methodSelect(true)}
        </FilterSheet>
      </div>
      </>
    );
  }

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <div className="docs-filter-bar">
        {searchPill}
        {methodSelect(false)}

        {/* Doc date-range, anchored to the far end. */}
        <div style={{ marginInlineStart: 'auto' }}>
          {dateRange('pill')}
        </div>
      </div>

      {hasChips && renderChips()}
    </div>
  );
}
